import { Box, Button, Column, Flex, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { AuthErrorBanner } from '../../shared';
import { MethodRadioOption } from './MethodRadioOption';
import {
  useChooseMethodForm,
  useChooseMethodFormLogic,
  type IChooseMethodStepValues,
  type TForgotPasswordMethod,
} from './useChooseMethodForm';

export type { TForgotPasswordMethod } from './useChooseMethodForm';

const FORM_WIDTH = 528;
const BUTTON_WIDTH = 296;
const BUTTON_RADIUS = 999;

export interface IChooseMethodStepProps {
  onCodeSent: (args: { identifier: string; method: TForgotPasswordMethod }) => void;
}

export const ChooseMethodStep = ({ onCodeSent }: IChooseMethodStepProps) => {
  const { methods } = useChooseMethodForm();

  return (
    <FormProvider {...methods}>
      <ChooseMethodStepInner onCodeSent={onCodeSent} />
    </FormProvider>
  );
};

const ChooseMethodStepInner = ({ onCodeSent }: IChooseMethodStepProps) => {
  const { control } = useFormContext<IChooseMethodStepValues>();
  const {
    submit,
    bannerRef,
    isPending,
    watchedMethod,
    bannerMessage,
    showBanner,
    emailRules,
    phoneRules,
    handleSelectMethod,
    handleInputChange,
    emailHasError,
    phoneHasError,
  } = useChooseMethodFormLogic({ onCodeSent });

  return (
    <form onSubmit={submit} data-testid="choose-method-form" noValidate>
      <Column align="center" style={{ marginTop: 16 }}>
        <Typography as="h1" size="heading_2" bold color={colors.neutral[600]} textAlign="center">
          Forgot Your Password?
        </Typography>

        <Box style={{ marginTop: 16 }}>
          <Typography variant="body1" color={colors.neutral[500]} textAlign="center">
            Enter your email or mobile number and we&apos;ll send a recovery code to get you back
            into your account.
          </Typography>
        </Box>
      </Column>

      <Flex justify="center">
        <Box style={{ width: FORM_WIDTH }}>
          <Box
            ref={bannerRef}
            style={{ marginTop: showBanner ? 24 : 0 }}
            data-testid="choose-method-banner-anchor"
          >
            <AuthErrorBanner visible={showBanner} message={bannerMessage} />
          </Box>

          <Column gap={8} style={{ marginTop: showBanner ? 16 : 48 }}>
            <Controller
              control={control}
              name="email"
              rules={emailRules}
              render={({ field }) => (
                <MethodRadioOption
                  value="email"
                  label="Email"
                  checked={watchedMethod === 'email'}
                  onSelect={() => handleSelectMethod('email')}
                  inputValue={field.value}
                  onInputChange={(value) => handleInputChange('email', value)}
                  inputPlaceholder="Enter your email"
                  inputType="email"
                  isError={emailHasError}
                  testId="forgot-method-email"
                  dataCy="forgot-method-email"
                />
              )}
            />

            <Controller
              control={control}
              name="phone"
              rules={phoneRules}
              render={({ field }) => (
                <MethodRadioOption
                  value="phone"
                  label="Mobile Number"
                  checked={watchedMethod === 'phone'}
                  onSelect={() => handleSelectMethod('phone')}
                  inputValue={field.value}
                  onInputChange={(value) => handleInputChange('phone', value)}
                  inputPlaceholder="Enter your mobile number"
                  inputType="tel"
                  isError={phoneHasError}
                  testId="forgot-method-phone"
                  dataCy="forgot-method-phone"
                />
              )}
            />
          </Column>

          <Flex justify="center" style={{ marginTop: 48 }}>
            <Button
              data-cy="btn-send-recovery-code"
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isPending}
              style={{ width: BUTTON_WIDTH, borderRadius: BUTTON_RADIUS }}
            >
              Send Recovery Code
            </Button>
          </Flex>
        </Box>
      </Flex>
    </form>
  );
};
